import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { tonUsdtPaymentsApi } from "@/api/tonUsdtPaymentsApi";

const TERMINAL_INVOICE_STATUSES = new Set([
  "confirmed",
  "expired",
  "cancelled",
  "manual_review",
  "rejected",
]);
const TON_USDT_INVOICE_PAGE_SIZE = 20;

export const TON_USDT_INVOICE_QUERY_KEYS = {
  detail: (invoiceId: string) => ["ton-usdt-invoice", invoiceId] as const,
  list: ["ton-usdt-invoices"] as const,
};

export const useTonUsdtInvoices = () =>
  useInfiniteQuery({
    queryKey: TON_USDT_INVOICE_QUERY_KEYS.list,
    queryFn: ({ pageParam }) =>
      tonUsdtPaymentsApi.listInvoices({ limit: TON_USDT_INVOICE_PAGE_SIZE, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, _allPages, lastPageParam) =>
      lastPage.has_more ? lastPageParam + lastPage.items.length : undefined,
    refetchInterval: (query) =>
      query.state.data?.pages.some((page) =>
        page.items.some((invoice) => !TERMINAL_INVOICE_STATUSES.has(invoice.status))
      )
        ? 5_000
        : false,
  });

export const useTonUsdtInvoiceStatus = (invoiceId: string | null) =>
  useQuery({
    queryKey: TON_USDT_INVOICE_QUERY_KEYS.detail(invoiceId ?? "pending"),
    queryFn: () => tonUsdtPaymentsApi.getInvoice(invoiceId!),
    enabled: invoiceId !== null,
    refetchInterval: (query) =>
      query.state.data && TERMINAL_INVOICE_STATUSES.has(query.state.data.status) ? false : 5_000,
    retry: false,
  });

export const useCancelTonUsdtInvoice = () => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: tonUsdtPaymentsApi.cancelInvoice,
    onSuccess: (invoice) => {
      queryClient.setQueryData(TON_USDT_INVOICE_QUERY_KEYS.detail(invoice.id), invoice);
      void queryClient.invalidateQueries({ queryKey: TON_USDT_INVOICE_QUERY_KEYS.list });
    },
  });

  return {
    cancelInvoice: mutation.mutateAsync,
    isCancellingInvoice: mutation.isPending,
  };
};
